import Relation from "./relation";
import ListView from "./list-view";
import List from "./list";
import Entity from "./entity";
import IRelation from "../interface/relation";
import IListView from "../interface/list-view";
import IList from "../interface/list";
import ILinkedInactive from "../interface/linked-inactive";
import ILinkedFieldOption from "../interface/linked-field-option";
import IEntityFilter from "../interface/entity-filter";
import IEntity from "../interface/entity";
import IDocumentValue from "../interface/document-value";
import IDocument from "../interface/document";
import ICollectionResponse from "../interface/collection-response";
import ICollectionArgs from "../interface/collection-args";
import ErrorCode from "../enum/error-code";
import CollectionClass from "../class/collection";

class CollectionInstance {

    private Collection: CollectionClass | null = null;

    /* Public Methods - Init */

    public async initByEntity(entityId: number): Promise<ICollectionResponse> {
        const views: IListView[] = await ListView.getByEntityId(entityId);
        return this.init(entityId, views);
    }

    public async initByList(listId: number): Promise<ICollectionResponse> {
        const list: IList | null = List.get(listId);
        if (list) {
            const views: IListView[] = await ListView.getByListId(listId);
            return this.init(list.entityId, views, list.id as number);
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async initByRelation(relationId: number, recordId: number): Promise<ICollectionResponse> {
        const relation: IRelation | null = Relation.getRelationTo(relationId);
        const list: IList | null = await List.getByRelation(relationId);
        if (list && relation) {
            const views: IListView[] = await ListView.getByListId(list.id as number);
            return this.init(list.entityId, views, list.id as number, relation, recordId);
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    /* Public Methods - State control */

    public clearState(): void {
        this.get().clearState();
    }

    public setView(viewId: number): void {
        this.get().setView(viewId);
    }

    public async setFilters(filters: IEntityFilter[], save: boolean = false): Promise<void> {
        await this.get().setFilters(filters, save);
    }

    public async toggleFieldSort(fieldId: number, save: boolean = true): Promise<void> {
        await this.get().toggleFieldSort(fieldId, save);
    }

    public async setFieldsOrder(order: number[]): Promise<void> {
        await this.get().setFieldsOrder(order);
    }

    public async setFieldWidth(fieldId: number, width: number): Promise<void> {
        await this.get().setFieldWidth(fieldId, width);
    }

    public async toggleFieldHidden(fieldId: number): Promise<void> {
        await this.get().toggleFieldHidden(fieldId);
    }

    public async toggleFieldReadonly(fieldId: number): Promise<void> {
        await this.get().toggleFieldReadonly(fieldId);
    }

    public async toggleLocked(fieldId: number): Promise<void> {
        await this.get().toggleLocked(fieldId);
    }

    public async setTextFilter(textPattern: string): Promise<void> {
        await this.get().setTextFilter(textPattern);
    }

    /* Public Methods - Feedback */

    public async setFeedback(id: number): Promise<void> {
        await this.get().setFeedback(id);
    }

    /* Public Methods - Documents control */

    public async documentCreate(): Promise<IDocument> {
        return await this.get().documentCreate();
    }

    public async documentUpdate(id: number, fieldId: number, value: IDocumentValue): Promise<IDocument> {
        return await this.get().documentUpdate(id, fieldId, value);
    }

    public async documentDelete(ids: number[]): Promise<number[]> {
        return await this.get().documentDelete(ids);
    }

    public async documentBulkUpdate(ids: number[], fieldId: number, value: IDocumentValue): Promise<IDocument[]> {
        return await this.get().documentBulkUpdate(ids, fieldId, value);
    }

    public getDefaultValue(id: number, fieldId: number): IDocumentValue | null {
        return this.get().getDefaultValue(id, fieldId);
    }

    /* Private Public Methods - Feedback */

    public async resetFeedback(): Promise<void> {
        if (this.Collection) {
            await this.Collection.resetFeedback();
        }
    }

    /* Private Public Methods - Notification handlers */

    public onDocumentsCreate(entityId: number, docs: IDocument[]): void {
        if (this.Collection && entityId === this.Collection.getEntityId()) {
            this.Collection.onDocumentsCreate(docs);
        }
    }

    public onDocumentsUpdate(entityId: number, docs: IDocument[]): void {
        if (this.Collection) {
            if (entityId === this.Collection.getEntityId()) {
                this.Collection.onDocumentsUpdate(docs);
            } else if (this.Collection.hasRelationTo(entityId)) {
                this.Collection.onRelatedDocuments(entityId, docs);
            }
        }
    }

    public onDocumentsDelete(entityId: number, docs: IDocument[]): void {
        if (this.Collection && entityId === this.Collection.getEntityId()) {
            this.Collection.onDocumentsDelete(docs);
        }
    }

    public async onDocumentsReset(entityId: number, docs: IDocument[]): Promise<void> {
        if (this.Collection && entityId === this.Collection.getEntityId()) {
            await this.Collection.onDocumentsUpdate(docs);
        }
    }

    public onEntityReset(entityIds: number[]): void {
        if (this.Collection) {
            const entityId: number = this.Collection.getEntityId();
            if (entityIds.includes(entityId)) {
                this.Collection.onEntityReset();
            }
        }
    }

    /* Public Methods - Linked Field calls */

    public isLinkedActive(fieldId: number, recordId: number): ILinkedInactive[] {
        return this.get().isLinkedActive(fieldId, recordId);
    }

    public async getLinkedOptions(fieldId: number, recordId: number): Promise<ILinkedFieldOption[] | null> {
        return this.get().getLinkedOptions(fieldId, recordId);
    }

    /* Public Methods - Special calls */

    public async getSimilarValues(fieldId: number, recordId: number, pattern: string): Promise<string[]> {
        return this.get().getSimilarValues(fieldId, recordId, pattern);
    }

    /* Private Public Methods - Utility */

    public clear(): void {
        if (this.Collection) {
            this.Collection.clear();
            this.Collection = null;
        }
    }

    /* Private Methods */

    private init(entityId: number, views: IListView[], listId: number | null = null, relation: IRelation | null = null, recordId: number | null = null): ICollectionResponse {
        const entity: IEntity | null = Entity.get(entityId);
        const view: IListView | null = views.find((v: IListView) => v.isDefault) || null;
        if (entity && view && Entity.hasAccess(entityId)) {
            const viewId: number = view.id as number;
            const args: ICollectionArgs = { entity, viewId, listId, relation, recordId };
            if (Entity.allowCache(entityId) && this.Collection && this.Collection.same(args)) {
                return this.Collection.getClientResponse();
            } else {
                this.clear();
                try {
                    this.Collection = new CollectionClass(args);
                    return this.Collection.getClientResponse();
                } catch (error) {
                    throw error;
                }
            }
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    private get(): CollectionClass {
        if (this.Collection) {
            return this.Collection;
        } else {
            throw ErrorCode.NotInitialized;
        }
    }

}

const Collection = new CollectionInstance();

export default Collection;